import { useState, useEffect, useMemo } from 'react';
import { Route, Plus, Trash2, RefreshCw, AlertCircle, ExternalLink, Settings, AlertTriangle, CheckCircle, Shield, ChevronDown, ChevronRight, Check, ToggleLeft, ToggleRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useToast } from '../../../components/common/ToastContainer';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import ApplyTemplateModal from '../../../components/policies/ApplyTemplateModal';
import PolicyDetailsModal from '../../../components/policies/PolicyDetailsModal';
import { routingPolicyService, applicationService } from '../../../services';
import type { RoutingPolicy, RoutingPolicyRule, ApplicationDefinition, ApplicationGroup, DgPolicyEdgeView } from '../../../services';
import { usePermissions } from '../../../hooks/usePermissions';

function isRuleIncomplete(rule: RoutingPolicyRule): boolean {
  if (rule.enabled === 0) return false;
  const needsTarget = ['route_via_interface', 'route_via_nexthop', 'route_via_tunnel'].includes(rule.action_type);
  if (needsTarget && !rule.action_target) return true;
  if (rule.action_type === 'rate_limit' && !rule.bandwidth_limit) return true;
  return false;
}

function isPolicyIncomplete(policy: RoutingPolicy): boolean {
  if (!policy.edge_id) return false;
  return (policy.rules || []).some(isRuleIncomplete);
}

function getIncompleteRuleCount(policy: RoutingPolicy): number {
  if (!policy.edge_id) return 0;
  return (policy.rules || []).filter(isRuleIncomplete).length;
}

interface RoutingPoliciesConfigProps {
  edgeId: string;
  tenantId?: string;
}

const RoutingPoliciesConfig = ({ edgeId, tenantId }: RoutingPoliciesConfigProps) => {
  const toast = useToast();
  const { canCreate, canDelete } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [assignedPolicies, setAssignedPolicies] = useState<RoutingPolicy[]>([]);
  const [removeConfirm, setRemoveConfirm] = useState<{ show: boolean; policy?: RoutingPolicy }>({ show: false });
  const [removing, setRemoving] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);

  // Policy details modal state
  const [selectedPolicy, setSelectedPolicy] = useState<RoutingPolicy | null>(null);
  const [isPolicyDetailsOpen, setIsPolicyDetailsOpen] = useState(false);
  const [applications, setApplications] = useState<ApplicationDefinition[]>([]);
  const [appGroups, setAppGroups] = useState<ApplicationGroup[]>([]);

  // Device Group policies state
  const [dgPolicies, setDgPolicies] = useState<DgPolicyEdgeView[]>([]);
  const [dgLoading, setDgLoading] = useState(true);
  const [dgExpanded, setDgExpanded] = useState(true);

  const pendingPolicies = useMemo(() =>
    assignedPolicies.filter(p => isPolicyIncomplete(p) || p.enabled === 0),
    [assignedPolicies]
  );

  const activePolicies = useMemo(() =>
    assignedPolicies.filter(p => !isPolicyIncomplete(p) && p.enabled === 1),
    [assignedPolicies]
  );

  useEffect(() => {
    fetchPolicies();
    fetchDgPolicies();
    fetchApplications();
  }, [edgeId]);

  const fetchPolicies = async (isRefresh = false) => {
    try {
      if (!isRefresh) setLoading(true);
      const allPolicies = await routingPolicyService.list({ pageSize: 100, tenantId });

      // Policies assigned specifically to this edge
      const assigned = allPolicies.items.filter(p => p.edge_id === edgeId);
      setAssignedPolicies(assigned);
    } catch (error) {
      console.error('Failed to fetch routing policies:', error);
      toast.error('Load Failed', 'Failed to load routing policies');
    } finally {
      if (!isRefresh) setLoading(false);
    }
  };

  const fetchDgPolicies = async () => {
    try {
      setDgLoading(true);
      const result = await routingPolicyService.getDeviceGroupPoliciesForEdge(edgeId);
      setDgPolicies(result);
    } catch (error) {
      console.error('Failed to fetch device group policies:', error);
    } finally {
      setDgLoading(false);
    }
  };

  const fetchApplications = async () => {
    try {
      const [appsResponse, groupsResponse] = await Promise.all([
        applicationService.list({ page: 1, pageSize: 100, tenantId }),
        applicationService.listGroups({ page: 1, pageSize: 100, tenantId }),
      ]);
      setApplications(appsResponse.items);
      setAppGroups(groupsResponse.items);
    } catch (error) {
      console.error('Failed to fetch applications:', error);
    }
  };

  const handleViewPolicy = async (policy: RoutingPolicy) => {
    try {
      const fullPolicy = await routingPolicyService.getById(policy.id);
      setSelectedPolicy(fullPolicy);
      setIsPolicyDetailsOpen(true);
    } catch (error) {
      console.error('Failed to fetch policy details:', error);
      toast.error('Load Failed', 'Failed to load policy details');
    }
  };

  const handleRemovePolicy = async () => {
    if (!removeConfirm.policy) return;

    try {
      setRemoving(true);
      await routingPolicyService.delete(removeConfirm.policy.id);
      toast.success('Policy Removed', 'Routing policy removed from this edge');
      setRemoveConfirm({ show: false });
      fetchPolicies();
    } catch (error: any) {
      console.error('Failed to remove policy:', error);
      toast.error('Removal Failed', error?.message || 'Failed to remove routing policy');
    } finally {
      setRemoving(false);
    }
  };

  const handleValidatePolicy = async (id: string) => {
    try {
      const validation = await routingPolicyService.validate(id);
      if (validation.valid) {
        toast.success('Validation Passed', 'Policy is valid!');
      } else {
        const message = `Validation failed: ${validation.errors.join(', ')}`;
        toast.error('Validation Failed', message);
      }
      if (validation.warnings.length > 0) {
        toast.warning('Validation Warnings', validation.warnings.join(', '));
      }
    } catch (error) {
      console.error('Failed to validate policy:', error);
      toast.error('Validation Error', 'Failed to validate policy');
    }
  };

  const handleToggleEnabled = async (policy: RoutingPolicy) => {
    const enabling = policy.enabled === 0;

    if (enabling) {
      // Validate before enabling
      try {
        const validation = await routingPolicyService.validate(policy.id);
        if (!validation.valid) {
          toast.error('Cannot Enable', `Fix errors first: ${validation.errors.join(', ')}`);
          return;
        }
        if (validation.warnings.length > 0) {
          toast.warning('Validation Warnings', validation.warnings.join(', '));
        }
      } catch (error) {
        toast.error('Validation Error', 'Failed to validate policy before enabling');
        return;
      }
    }

    try {
      await routingPolicyService.update(policy.id, { enabled: enabling ? 1 : 0 });
      toast.success(enabling ? 'Policy Enabled' : 'Policy Disabled',
        `"${policy.name}" has been ${enabling ? 'enabled' : 'disabled'}`);
      fetchPolicies();
    } catch (error: any) {
      toast.error('Update Failed', error?.message || 'Failed to update policy');
    }
  };

  const handleTemplateApplied = () => {
    setShowTemplateModal(false);
    fetchPolicies();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Route className="w-5 h-5 text-primary-600" />
          <h3 className="text-lg font-semibold text-gray-900">Policies</h3>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => { fetchPolicies(); fetchDgPolicies(); }}
            className="btn-secondary inline-flex items-center space-x-2 text-sm"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
          <Link
            to="/routing-policies"
            className="btn-secondary inline-flex items-center space-x-2 text-sm"
          >
            <ExternalLink className="w-4 h-4" />
            <span>Manage Policy Templates</span>
          </Link>
          {canCreate && (
          <button
            onClick={() => setShowTemplateModal(true)}
            className="btn-primary inline-flex items-center space-x-2 text-sm"
          >
            <Plus className="w-4 h-4" />
            <span>Add Routing Policy</span>
          </button>
          )}
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-blue-400 mr-2 mt-0.5" />
          <div className="text-sm text-blue-700">
            <p className="font-medium">Policy Overview</p>
            <p className="mt-1">
              Apply policy templates for edge-specific routing, and view device group access policies that affect VPN traffic on this edge.
            </p>
          </div>
        </div>
      </div>

      {/* Suricata dependency banner — show when any rule requires Suricata */}
      {assignedPolicies.some(p => (p.rules || []).some(r => r.requires_suricata === 1)) && (
        <div className="bg-amber-50 border-l-4 border-amber-400 p-4">
          <div className="flex items-start">
            <AlertTriangle className="w-5 h-5 text-amber-500 mr-2 mt-0.5" />
            <div className="text-sm text-amber-700">
              <p className="font-medium">Suricata Required</p>
              <p className="mt-1">
                Some routing policy rules use domain-based detection or DSCP marking, which require Suricata IDS/IPS to be enabled.
                If Suricata is disabled, these rules will fall back to port-only matching.
              </p>
              <Link
                to={`/edges/${edgeId}/config`}
                className="inline-flex items-center text-amber-800 hover:text-amber-900 font-medium mt-1"
              >
                Configure Suricata
                <ExternalLink className="w-3 h-3 ml-1" />
              </Link>
            </div>
          </div>
        </div>
      )}

      {assignedPolicies.length === 0 ? (
        <div className="text-sm text-gray-500 bg-gray-50 rounded-lg p-4 text-center">
          No policies applied to this edge. Click "Add Routing Policy" to apply a template.
        </div>
      ) : (
        <>
          {/* Pending Configuration */}
          {pendingPolicies.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-amber-700 mb-3 flex items-center space-x-2">
                <AlertTriangle className="w-4 h-4" />
                <span>Pending Configuration ({pendingPolicies.length})</span>
              </h4>
              <div className="space-y-2">
                {pendingPolicies.map((policy) => {
                  const incomplete = getIncompleteRuleCount(policy);
                  return (
                    <div
                      key={policy.id}
                      className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-300 hover:border-amber-400 transition-colors cursor-pointer"
                      onClick={() => handleViewPolicy(policy)}
                    >
                      <div className="flex items-center space-x-3">
                        <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                        <div>
                          <div className="font-medium text-gray-900">{policy.name}</div>
                          {incomplete > 0 ? (
                            <div className="text-xs text-amber-700">
                              {incomplete} rule{incomplete > 1 ? 's' : ''} missing edge-specific configuration
                            </div>
                          ) : policy.description ? (
                            <div className="text-xs text-gray-500">{policy.description}</div>
                          ) : null}
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className="text-xs text-gray-500">
                          Priority: {policy.priority} | {policy.rules?.length || 0} rules
                        </span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${incomplete > 0 ? 'bg-amber-200 text-amber-800' : 'bg-gray-100 text-gray-600'
                          }`}>
                          {incomplete > 0 ? 'Incomplete' : 'Disabled'}
                        </span>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleToggleEnabled(policy); }}
                          className="p-1 hover:bg-amber-100 rounded text-gray-500"
                          title={policy.enabled === 0 ? 'Enable Policy (validates first)' : 'Disable Policy'}
                        >
                          {policy.enabled === 0 ? <ToggleLeft className="w-5 h-5" /> : <ToggleRight className="w-5 h-5 text-green-600" />}
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleValidatePolicy(policy.id); }}
                          className="p-1 hover:bg-amber-100 rounded text-green-600"
                          title="Validate Policy"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleViewPolicy(policy); }}
                          className="p-1 hover:bg-amber-100 rounded text-blue-600"
                          title="Configure Policy Rules"
                        >
                          <Settings className="w-4 h-4" />
                        </button>
                        {canDelete && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setRemoveConfirm({ show: true, policy }); }}
                          className="p-1 hover:bg-amber-100 rounded text-red-600"
                          title="Delete this policy"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Active Routing Policies */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center space-x-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span>Active Routing Policies ({activePolicies.length})</span>
            </h4>
            {activePolicies.length === 0 ? (
              <div className="text-sm text-gray-500 bg-gray-50 rounded-lg p-4 text-center">
                {pendingPolicies.length > 0
                  ? 'No fully configured policies. Configure pending policies above.'
                  : 'No active policies.'}
              </div>
            ) : (
              <div className="space-y-2">
                {activePolicies.map((policy) => (
                  <div
                    key={policy.id}
                    className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200 hover:border-green-300 transition-colors cursor-pointer"
                    onClick={() => handleViewPolicy(policy)}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 rounded-full bg-green-500" />
                      <div>
                        <div className="font-medium text-gray-900">{policy.name}</div>
                        {policy.description && (
                          <div className="text-xs text-gray-500">{policy.description}</div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-xs text-gray-500">
                        Priority: {policy.priority} | {policy.rules?.length || 0} rules
                      </span>
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                        Active
                      </span>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleToggleEnabled(policy); }}
                        className="p-1 hover:bg-green-100 rounded"
                        title="Disable Policy"
                      >
                        <ToggleRight className="w-5 h-5 text-green-600" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleValidatePolicy(policy.id); }}
                        className="p-1 hover:bg-green-100 rounded text-green-600"
                        title="Validate Policy"
                      >
                        <CheckCircle className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleViewPolicy(policy); }}
                        className="p-1 hover:bg-green-100 rounded text-blue-600"
                        title="Edit Policy Rules"
                      >
                        <Settings className="w-4 h-4" />
                      </button>
                      {canDelete && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setRemoveConfirm({ show: true, policy }); }}
                        className="p-1 hover:bg-green-100 rounded text-red-600"
                        title="Delete this policy"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Device Group Access Policies */}
      {!dgLoading && dgPolicies.length > 0 && (
        <DeviceGroupPoliciesSection
          dgPolicies={dgPolicies}
          expanded={dgExpanded}
          onToggle={() => setDgExpanded(!dgExpanded)}
          applications={applications}
          appGroups={appGroups}
        />
      )}

      {/* Apply Template Modal */}
      <ApplyTemplateModal
        isOpen={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        edgeId={edgeId}
        tenantId={tenantId}
        onApplied={handleTemplateApplied}
      />

      {/* Policy Details Modal (for editing edge-specific rules) */}
      {selectedPolicy && (
        <PolicyDetailsModal
          isOpen={isPolicyDetailsOpen}
          onClose={() => { setIsPolicyDetailsOpen(false); setSelectedPolicy(null); }}
          policy={selectedPolicy}
          applications={applications}
          appGroups={appGroups}
          onRefresh={() => {
            fetchPolicies(true);
            if (selectedPolicy) {
              routingPolicyService.getById(selectedPolicy.id).then(fullPolicy => setSelectedPolicy(fullPolicy));
            }
          }}
          edgeId={edgeId}
        />
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={removeConfirm.show}
        onClose={() => setRemoveConfirm({ show: false })}
        onConfirm={handleRemovePolicy}
        title="Delete Routing Policy"
        message={`Are you sure you want to delete "${removeConfirm.policy?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        isLoading={removing}
      />
    </div>
  );
};

// ==========================================
// Device Group Access Policies (Read-Only)
// ==========================================

function getActionColor(actionType: string): string {
  switch (actionType) {
    case 'allow': return 'bg-green-100 text-green-700';
    case 'drop': return 'bg-red-100 text-red-700';
    case 'rate_limit': return 'bg-purple-100 text-purple-700';
    default: return 'bg-gray-100 text-gray-700';
  }
}

function getActionLabel(actionType: string): string {
  switch (actionType) {
    case 'allow': return 'Allow';
    case 'drop': return 'Drop';
    case 'rate_limit': return 'Rate Limit';
    default: return actionType;
  }
}

interface DeviceGroupPoliciesSectionProps {
  dgPolicies: DgPolicyEdgeView[];
  expanded: boolean;
  onToggle: () => void;
  applications: ApplicationDefinition[];
  appGroups: ApplicationGroup[];
}

function DeviceGroupPoliciesSection({ dgPolicies, expanded, onToggle, applications, appGroups }: DeviceGroupPoliciesSectionProps) {
  // Count unreachable rules across all policies
  const unreachableCount = dgPolicies.reduce((sum, dp) =>
    sum + dp.rules.filter(r => r.reachable === false).length, 0);
  const unreachablePolicyCount = dgPolicies.filter(dp =>
    dp.rules.some(r => r.reachable === false)).length;

  const getAppName = (appDefId?: string | null) => {
    if (!appDefId) return null;
    return applications.find(a => a.id === appDefId)?.name || null;
  };

  const getAppGroupName = (groupId?: string | null) => {
    if (!groupId) return null;
    return appGroups.find(g => g.id === groupId)?.name || null;
  };

  return (
    <div className="border border-gray-200 rounded-lg">
      {/* Section Header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center space-x-2">
          <Shield className="w-5 h-5 text-indigo-500" />
          <h4 className="text-sm font-semibold text-gray-900">
            Device Group Access Policies ({dgPolicies.length})
          </h4>
        </div>
        {expanded ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-4">
          {/* Unreachable warning banner */}
          {unreachableCount > 0 && (
            <div className="bg-amber-50 border-l-4 border-amber-400 p-3">
              <div className="flex items-start">
                <AlertTriangle className="w-4 h-4 text-amber-500 mr-2 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-amber-700">
                  {unreachableCount} destination{unreachableCount !== 1 ? 's' : ''} across {unreachablePolicyCount} {unreachablePolicyCount !== 1 ? 'policies' : 'policy'} {unreachableCount !== 1 ? 'are' : 'is'} not reachable from this edge.
                  Traffic to unreachable subnets will be dropped or misrouted.
                </div>
              </div>
            </div>
          )}

          {/* Per-policy cards */}
          {dgPolicies.map((dp) => (
            <div key={dp.policy.id} className={`border border-gray-200 rounded-lg p-4 space-y-3 ${dp.policy.enabled === 0 ? 'opacity-60' : ''}`}>
              {/* Policy header */}
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-gray-900">{dp.policy.name}</span>
                    {dp.policy.enabled === 0 && (
                      <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-500">Disabled</span>
                    )}
                    <span className="text-xs text-gray-500">
                      [{dp.groups.map(g => g.name).join(', ')}]
                    </span>
                  </div>
                  {dp.policy.description && (
                    <p className="text-xs text-gray-500 mt-1">{dp.policy.description}</p>
                  )}
                </div>
                <div className="flex items-center space-x-2 flex-shrink-0">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${dp.policy.default_action === 'deny' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                    }`}>
                    {dp.policy.default_action === 'deny' ? 'Deny Default' : 'Allow Default'}
                  </span>
                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                    Priority: {dp.policy.priority}
                  </span>
                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                    {dp.rules.length} rule{dp.rules.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>

              {/* Groups line */}
              <div className="text-xs text-gray-500">
                Groups: {dp.groups.map(g => `${g.name}${g.subnet_cidr ? ` (${g.subnet_cidr})` : ''}`).join(', ')}
              </div>

              {/* Rules list */}
              {dp.rules.length > 0 && (
                <div className="space-y-1">
                  {dp.rules.map((rule) => {
                    const appName = getAppName(rule.app_definition_id);
                    const groupName = getAppGroupName(rule.app_group_id);
                    return (
                      <div
                        key={rule.id}
                        className={`flex items-center justify-between py-1.5 px-2 rounded text-sm ${rule.enabled === 0 ? 'opacity-50' : ''
                          }`}
                      >
                        <div className="flex items-center space-x-2 min-w-0">
                          <span className={`px-1.5 py-0.5 rounded text-xs font-medium flex-shrink-0 ${getActionColor(rule.action_type)}`}>
                            {getActionLabel(rule.action_type)}
                          </span>
                          {rule.destination_subnet && (
                            <span className="text-xs text-gray-700 font-mono">{rule.destination_subnet}</span>
                          )}
                          {appName && (
                            <span className="text-xs text-gray-500 truncate">App: {appName}</span>
                          )}
                          {groupName && (
                            <span className="text-xs text-gray-500 truncate">Group: {groupName}</span>
                          )}
                          {rule.action_type === 'rate_limit' && rule.bandwidth_limit && (
                            <span className="text-xs text-purple-600">{rule.bandwidth_limit} Mbps</span>
                          )}
                        </div>
                        <div className="flex-shrink-0 ml-2">
                          {rule.reachable === true && (
                            <span title={`Reachable via ${rule.reachability_source}`}>
                              <Check className="w-4 h-4 text-green-500" />
                            </span>
                          )}
                          {rule.reachable === false && (
                            <span title="Destination not reachable from this edge">
                              <AlertTriangle className="w-4 h-4 text-amber-500" />
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Deny-default footer */}
              {dp.policy.default_action === 'deny' && (
                <div className="text-xs text-gray-400 border-t border-gray-100 pt-2">
                  Catch-all: all unmatched traffic from this group is denied
                </div>
              )}
            </div>
          ))}

          {/* Manage link */}
          <div className="text-right">
            <Link
              to="/routing-policies"
              className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              Manage Policies
              <ExternalLink className="w-3 h-3 ml-1" />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

export default RoutingPoliciesConfig;
